import React, { useState, useEffect } from "react";
import {
  Card,
  Snackbar,
  Alert,
  CircularProgress,
  Box,
  Divider,
  Grid,
  IconButton,
} from "@mui/material";
import {
  CheckCircle,
  Warning,
  Refresh,
  Error as ErrorIcon,
  Info,
  Timer,
  GridView,
  Groups,
  TableChart,
} from "@mui/icons-material";

// Material Dashboard 2 React components
import DashboardLayout from "app/layout/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/layout/DashboardNavbar";
import MDBox from "shared/components/MDBox";
import MDTypography from "shared/components/MDTypography";
import MDButton from "shared/components/MDButton";

// Componentes personalizados
import HeaderVipCame from "./components/HeaderVipCame";
import TableVipCame from "./components/TableVipCame";
import { useTableVipCame } from "./hooks/useTableVipCame";
import GroupsTable from "./components/TableVipCame/GroupsTable";

const VipCame = () => {
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
    action: null,
    autoHideDuration: 6000,
  });

  // Usar hook personalizado
  const {
    groups,
    selectedGroup,
    groupVehicles,
    parkingVehicles,
    loading: dataLoading,
    error: dataError,
    stats,
    occupancyKnown,
    occupancyWarning,
    showNoData,
    selectGroup,
    refreshData,
    groupName,
    isLoading,
    hasData,
    loadAllVehiclesData,
    isAllVehiclesMode,
    cuposInfo,
    groupsWithQuotas,
    selectedGroupQuota,
    allGroupsVehicles = [],
  } = useTableVipCame();

  // Estado para controlar el modo de vista
  const [viewMode, setViewMode] = useState(() => {
    return isAllVehiclesMode ? "all" : "groups";
  });

  // Estado para mostrar/ocultar la tabla de grupos
  const [showGroupsTable, setShowGroupsTable] = useState(true);

  // Sincronizar viewMode con isAllVehiclesMode del hook
  useEffect(() => {
    setViewMode(isAllVehiclesMode ? "all" : "groups");
  }, [isAllVehiclesMode]);

  // ========== MANEJO DE NOTIFICACIONES ==========
  const showNotification = (message, severity = "success", options = {}) => {
    setSnackbar({
      open: true,
      message,
      severity,
      action: options.action || null,
      autoHideDuration: options.persist ? null : options.duration || 6000,
    });
  };

  const showSuccess = (message, options = {}) => {
    showNotification(message, "success", options);
  };

  const showError = (message, options = {}) => {
    showNotification(message, "error", options);
  };

  const showWarning = (message, options = {}) => {
    showNotification(message, "warning", options);
  };

  const showInfo = (message, options = {}) => {
    showNotification(message, "info", options);
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // ========== MANEJADORES DE EVENTOS ==========
  const handleSelectGroup = async (group) => {
    try {
      if (!group) {
        await handleShowAllVehicles();
        return;
      }

      const result = await selectGroup(group);
      if (result?.canceled) return;
      if (!result?.success) throw new Error(result?.error || "No se pudo consultar el grupo");
      setShowGroupsTable(false);
      if (!result.occupancyKnown) {
        showWarning(result.occupancyError);
        return;
      }
      showSuccess(`Grupo "${group.name}" seleccionado`, {
        duration: 3000,
      });
    } catch (error) {
      showError(`Error al seleccionar grupo: ${error.message}`, {
        action: (
          <IconButton size="small" color="inherit" onClick={() => handleSelectGroup(group)}>
            <Refresh fontSize="small" />
          </IconButton>
        ),
      });
    }
  };

  const handleShowAllVehicles = async () => {
    try {
      showInfo("Cargando todos los vehículos...", { duration: 2000 });
      const result = await loadAllVehiclesData();
      if (result?.canceled) return;
      if (!result?.success) throw new Error(result?.error || "No se pudo cargar el listado");
      if (!result.occupancyKnown) {
        showWarning(result.occupancyError);
        return;
      }
      setShowGroupsTable(true);
      showSuccess("Todos los vehículos cargados correctamente");
    } catch (error) {
      showError(`Error al cargar todos los vehículos: ${error.message}`, {
        action: (
          <IconButton size="small" color="inherit" onClick={handleShowAllVehicles}>
            <Refresh fontSize="small" />
          </IconButton>
        ),
      });
    }
  };

  const handleRefresh = async () => {
    try {
      showInfo("Actualizando datos...", { duration: 2000 });
      const result = await refreshData();
      if (result?.canceled) return;
      if (!result?.success) throw new Error(result?.error || "No se pudo actualizar el listado");
      if (!result.occupancyKnown) {
        showWarning(result.occupancyError);
        return;
      }
      showSuccess("Datos actualizados correctamente");
    } catch (error) {
      showError(`Error al actualizar datos: ${error.message}`, {
        action: (
          <IconButton size="small" color="inherit" onClick={handleRefresh}>
            <Refresh fontSize="small" />
          </IconButton>
        ),
      });
    }
  };

  const handleRetryLoad = () => {
    showInfo("Reintentando carga de datos...", { duration: 2000 });
    handleRefresh();
  };

  // ========== EFECTOS ==========
  useEffect(() => {
    if (dataError && !showNoData.groupVehicles && !showNoData.parkingVehicles) {
      showError(dataError, {
        action: (
          <IconButton size="small" color="inherit" onClick={handleRefresh}>
            <Refresh fontSize="small" />
          </IconButton>
        ),
      });
    }
  }, [dataError, showNoData.groupVehicles, showNoData.parkingVehicles]);

  useEffect(() => {
    if (showNoData.groupVehicles || showNoData.parkingVehicles) {
      showWarning(
        "La carga de datos está tomando más tiempo de lo esperado. " +
          "Esto puede deberse a problemas de conexión o carga del servidor.",
        {
          persist: false,
          duration: 8000,
          action: (
            <IconButton size="small" color="inherit" onClick={handleRefresh}>
              <Refresh fontSize="small" />
            </IconButton>
          ),
        }
      );
    }
  }, [showNoData.groupVehicles, showNoData.parkingVehicles]);

  // ========== RENDERIZADO ==========
  return (
    <DashboardLayout>
      <DashboardNavbar />

      {/* Notificación Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.autoHideDuration}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={handleCloseSnackbar}
          action={snackbar.action}
          sx={{
            width: "100%",
            maxWidth: "500px",
            boxShadow: 3,
            borderRadius: "8px",
          }}
          iconMapping={{
            success: <CheckCircle fontSize="inherit" />,
            error: <ErrorIcon fontSize="inherit" />,
            warning: <Warning fontSize="inherit" />,
            info: <Info fontSize="inherit" />,
          }}
        >
          <MDTypography variant="body2" fontWeight="medium">
            {snackbar.message}
          </MDTypography>
        </Alert>
      </Snackbar>

      {/* Contenido principal */}
      <MDBox py={3}>
        {/* Sección de header con selector de grupo */}
        <MDBox mb={4}>
          <HeaderVipCame
            groups={groups}
            selectedGroup={selectedGroup}
            onSelectGroup={handleSelectGroup}
            onRefresh={handleRefresh}
            loading={isLoading}
            stats={stats}
            occupancyKnown={occupancyKnown}
            hasError={!!dataError}
            showNoData={showNoData.groupVehicles || showNoData.parkingVehicles}
            cuposInfo={cuposInfo}
            selectedGroupQuota={selectedGroupQuota}
          />
        </MDBox>

        {/* Botones para cambiar modo de vista */}
        <MDBox mb={3}>
          <Card
            sx={{
              p: 2,
              borderRadius: "12px",
              backgroundColor: "background.paper",
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <MDTypography variant="h6" color="textPrimary" fontWeight="medium">
                  Modo de Vista
                </MDTypography>
                <MDTypography variant="body2" color="textSecondary">
                  Seleccione qué datos desea visualizar
                </MDTypography>
              </Grid>
              <Grid item xs={12} md={6}>
                <MDBox display="flex" gap={2} justifyContent="flex-end">
                  <MDButton
                    variant={isAllVehiclesMode ? "contained" : "outlined"}
                    color="secondary"
                    startIcon={<GridView />}
                    onClick={handleShowAllVehicles}
                    disabled={isLoading || isAllVehiclesMode}
                    sx={{
                      borderRadius: "8px",
                      textTransform: "none",
                      fontWeight: 600,
                    }}
                  >
                    Todos los Vehículos
                  </MDButton>
                  <MDButton
                    variant={!isAllVehiclesMode && selectedGroup ? "contained" : "outlined"}
                    color="primary"
                    startIcon={<Groups />}
                    onClick={() => {
                      if (groups.length > 0 && !selectedGroup) {
                        handleSelectGroup(groups[0]);
                      }
                    }}
                    disabled={isLoading || groups.length === 0}
                    sx={{
                      borderRadius: "8px",
                      textTransform: "none",
                      fontWeight: 600,
                    }}
                  >
                    {selectedGroup ? `Grupo: ${selectedGroup.name}` : "Seleccionar Grupo"}
                  </MDButton>
                </MDBox>
              </Grid>
            </Grid>
          </Card>
        </MDBox>

        {/* Separador visual */}
        {(hasData || dataError || showNoData.groupVehicles || showNoData.parkingVehicles) && (
          <MDBox mb={3}>
            <Divider
              sx={{
                borderColor: isAllVehiclesMode ? "secondary.light" : "rgba(0,0,0,0.1)",
                borderWidth: 1,
                opacity: hasData ? 1 : 0.5,
              }}
            />
          </MDBox>
        )}

        {/* Sección de tablas de vehículos */}
        <MDBox>
          <Card
            sx={{
              mb: 3,
              borderRadius: "12px",
              overflow: "hidden",
              boxShadow: "0 4px 20px 0 rgba(0,0,0,0.1)",
              border: dataError
                ? "1px solid #f44336"
                : showNoData.groupVehicles || showNoData.parkingVehicles
                ? "1px solid #ff9800"
                : isAllVehiclesMode
                ? "1px solid #5c6bc0"
                : "1px solid #e0e0e0",
            }}
          >
            <MDBox p={3} bgcolor="background.paper">
              {occupancyWarning && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {occupancyWarning}
                </Alert>
              )}
              <TableVipCame
                groupVehicles={groupVehicles}
                parkingVehicles={parkingVehicles}
                loading={isLoading}
                error={dataError}
                groupName={groupName}
                showNoData={showNoData}
                onError={handleRetryLoad}
                isAllVehiclesMode={isAllVehiclesMode}
                occupancyKnown={occupancyKnown}
              />
            </MDBox>
          </Card>

          {/* Tabla de Grupos - Solo se muestra cuando NO hay un grupo seleccionado */}
          {showGroupsTable && !selectedGroup && groupsWithQuotas.length > 0 && (
            <MDBox mb={4}>
              <Card
                sx={{
                  borderRadius: "12px",
                  overflow: "hidden",
                  boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
                }}
              >
                <MDBox
                  p={2}
                  sx={{
                    backgroundColor: "#f5f5f5",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <MDBox display="flex" alignItems="center" gap={1}>
                    <TableChart sx={{ color: "#4361ee", fontSize: "1.25rem" }} />
                    <MDTypography variant="subtitle1" fontWeight="bold" color="text.primary">
                      Resumen de Grupos y Cupos
                    </MDTypography>
                  </MDBox>
                  <MDTypography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: "0.7rem" }}
                  >
                    Listado completo de todos los grupos con sus cupos asignados y estadísticas de
                    vehículos
                  </MDTypography>
                </MDBox>
                <MDBox p={1}>
                  <GroupsTable
                    groups={groupsWithQuotas}
                    allVehicles={groupVehicles}
                    loading={isLoading}
                    occupancyKnown={occupancyKnown}
                  />
                </MDBox>
              </Card>
            </MDBox>
          )}

          {/* Estadísticas detalladas */}
          {occupancyKnown &&
            hasData &&
            !showNoData.groupVehicles &&
            !showNoData.parkingVehicles &&
            !dataError && (
              <MDBox
                mt={2}
                p={2}
                bgcolor={
                  isAllVehiclesMode ? "rgba(92, 107, 192, 0.05)" : "rgba(25, 118, 210, 0.05)"
                }
                borderRadius="8px"
                border={`1px solid ${
                  isAllVehiclesMode ? "rgba(92, 107, 192, 0.1)" : "rgba(25, 118, 210, 0.1)"
                }`}
              >
                <Grid container spacing={2}>
                  {isAllVehiclesMode ? (
                    // Estadísticas para modo "Todos" - Mostrar información de cupos resumida
                    cuposInfo &&
                    cuposInfo.cupo_total > 0 && (
                      <>
                        <Grid item xs={12} sm={6} md={3}>
                          <MDBox textAlign="center">
                            <MDTypography variant="h6" color="info" fontWeight="bold">
                              {cuposInfo.cupo_total}
                            </MDTypography>
                            <MDTypography variant="caption" color="textSecondary">
                              Cupos Totales
                            </MDTypography>
                          </MDBox>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <MDBox textAlign="center">
                            <MDTypography variant="h6" color="warning" fontWeight="bold">
                              {cuposInfo.cupos_usados}
                            </MDTypography>
                            <MDTypography variant="caption" color="textSecondary">
                              Cupos Usados
                            </MDTypography>
                          </MDBox>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <MDBox textAlign="center">
                            <MDTypography
                              variant="h6"
                              color={cuposInfo.cupos_disponibles === 0 ? "error" : "success"}
                              fontWeight="bold"
                            >
                              {cuposInfo.cupos_disponibles}
                            </MDTypography>
                            <MDTypography variant="caption" color="textSecondary">
                              Cupos Disponibles
                            </MDTypography>
                          </MDBox>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <MDBox textAlign="center">
                            <MDTypography variant="h6" color="primary" fontWeight="bold">
                              {groupVehicles.length}
                            </MDTypography>
                            <MDTypography variant="caption" color="textSecondary">
                              Total Vehículos
                            </MDTypography>
                          </MDBox>
                        </Grid>
                      </>
                    )
                  ) : (
                    // Estadísticas para modo "Grupos"
                    <>
                      <Grid item xs={12} sm={6} md={3}>
                        <MDBox textAlign="center">
                          <MDTypography variant="h6" color="primary" fontWeight="bold">
                            {groupVehicles.length}
                          </MDTypography>
                          <MDTypography variant="caption" color="textSecondary">
                            Vehículos registrados
                          </MDTypography>
                        </MDBox>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <MDBox textAlign="center">
                          <MDTypography variant="h6" color="success" fontWeight="bold">
                            {stats.parkingVehicles}
                          </MDTypography>
                          <MDTypography variant="caption" color="textSecondary">
                            En parqueadero
                          </MDTypography>
                        </MDBox>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <MDBox textAlign="center">
                          <MDTypography variant="h6" color="warning" fontWeight="bold">
                            {groupVehicles.length - stats.parkingVehicles}
                          </MDTypography>
                          <MDTypography variant="caption" color="textSecondary">
                            Fuera del parqueadero
                          </MDTypography>
                        </MDBox>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <MDBox textAlign="center">
                          <MDTypography variant="h6" color="info" fontWeight="bold">
                            {selectedGroupQuota}
                          </MDTypography>
                          <MDTypography variant="caption" color="textSecondary">
                            Cupos del Grupo
                          </MDTypography>
                        </MDBox>
                      </Grid>
                    </>
                  )}
                </Grid>
              </MDBox>
            )}
        </MDBox>
      </MDBox>
    </DashboardLayout>
  );
};

export default VipCame;
